/** @format **/

/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import {
  AddressV3,
  AuthorizationResponse,
  CartItem,
  Chain,
  CommerceAddress,
  ContactForm,
  Environment,
  KillSwitches,
  PaymentError,
  PaymentsAddress,
  QueryParameters,
} from '@shared/data-access/interfaces';
import { CheckoutAnalyticsService } from './checkout.analytics.service';
import { CookieService } from '../cookie.service';
import { LoadingOverlayService } from '../loading-overlay.service';
import { WINDOW } from '../window/window.service';
import { UtilityService } from '../utility.service';
import { AvsService } from './avs.service';
import { Store } from '../app.store';
import { ScorecardPlusConfigService } from '@scorecard/config';

import { BehaviorSubject, iif, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  first,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  CartCheckoutState,
  CCAddressV2,
  Checkout,
  IGift,
} from '@cart-checkout/data-access/interfaces';
import { CheckoutErrorLogKeys, CheckoutPaths } from '@cart-checkout/data-access/enums';
import { PaymentService } from './payment.service';
import { CheckoutDao } from './checkout.dao';
import { TracerService } from '@shared/ssr-core/data-access';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  public checkoutKey = null;
  private readonly enablePlaceOrderButton$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  // set a default value
  public independencePayErrors: BehaviorSubject<any> = new BehaviorSubject([
    { code: 'XXX', systemError: 'default', message: '' },
  ]);

  public independencePayErrors$ = this.independencePayErrors.asObservable();

  private readonly scorecardPlusConfigService = inject(ScorecardPlusConfigService);

  constructor(
    private readonly chain: Chain,
    private readonly checkoutAnalyticsService: CheckoutAnalyticsService,
    private readonly checkoutDao: CheckoutDao,
    private readonly cookieService: CookieService,
    private readonly environment: Environment,
    private readonly store: Store<CartCheckoutState>,
    private readonly utilityService: UtilityService,
    private readonly avsService: AvsService,
    private readonly loadingOverlayService: LoadingOverlayService,
    private readonly paymentService: PaymentService,
    private readonly tracerService: TracerService,
    @Inject(WINDOW) protected readonly window: Window,
  ) {}

  // region Wrapper Methods

  public createCheckoutWrapper$(): Observable<Checkout> {
    return this.createModernCheckout$();
  }

  public getCheckout$(checkoutKey: string): Observable<Checkout> {
    return this.getModernCheckout$(checkoutKey);
  }

  public getSubmittedCheckout$(checkoutKey: string): Observable<Checkout> {
    return this.getSubmittedModernCheckout$(checkoutKey);
  }

  public submitCheckoutWrapper$(checkout: Checkout, flow?: string): Observable<Checkout> {
    return this.submitCheckout$(checkout, flow);
  }

  public addAndFinalizeAddressWrapper$(
    checkout: Checkout,
    addressType: string,
    userContactData: any,
    userAddressData: any,
  ): Observable<Checkout> {
    return this.addAndFinalizeAddress$(checkout, addressType, userContactData, userAddressData);
  }

  public getOrCreateOrderWithOnlyOrderSummary$(checkoutKey: string): Observable<Checkout> {
    return checkoutKey ? this.getModernCheckout$(checkoutKey) : this.createModernCheckout$();
  }

  public finalizeAddressesWrapper$(): Observable<null> {
    return of(null);
  }

  public submitTipAmountWrapper$(checkoutKey: string, tipAmount: string): Observable<Checkout> {
    return this.submitModernTipAmount(checkoutKey, tipAmount);
  }

  // endregion

  // region Legacy Checkout

  /**
   * @description create a checkout object.
   */
  public createCheckout$(): Observable<Checkout> {
    return this.store.selectDefined('ENVIRONMENT_KILLSWITCH_VALUES').pipe(
      first(),
      switchMap((killswitch: KillSwitches) => {
        return this.checkoutDao.createCheckout$(this.getCheckoutVersion(killswitch)).pipe(
          tap((checkoutResponse: Checkout) => {
            this.store.set('CHECKOUT', checkoutResponse);
          }),
        );
      }),
    );
  }

  /* istanbul ignore next */
  public createCheckoutAsPromise() {
    return this.createCheckoutWrapper$()
      .pipe(
        tap((checkoutResponse: Checkout) => {
          this.store.set('CHECKOUT', checkoutResponse);
        }),
      )
      .toPromise();
  }

  private getCheckoutVersion(killswitch: KillSwitches): 'v1' | 'v2' {
    return this.chain.chainIdentifierAbbr === 'pl' ? 'v1' : 'v2';
  }

  /**
   * @description
   * Returns a redirect URL string given an error from engine
   * TODO: Add unit testing since this is no longer a private method in the checkout resolve guard
   *
   * @param error
   * @param currentUrlPath
   * @param isOrderConfirmationPage
   * @param isPayPalLandingPage
   */
  public getDialogMessageRedirectPathFromError(
    error: HttpErrorResponse | any,
    currentUrlPath: string,
    isOrderConfirmationPage = false,
    isPayPalLandingPage = false,
  ): string {
    if (
      typeof error?.error?.ui_messaging?.button?.redirect_uri === 'string' &&
      error.error.ui_messaging.button.redirect_uri.trim() !== ''
    ) {
      // There is a redirect path, check to see if it's something other than Cart if in SinglePageCheckout
      if (
        currentUrlPath?.toLowerCase().includes(CheckoutPaths.SINGLEPAGE.toLowerCase()) &&
        !error.error.ui_messaging.button.redirect_uri
          .toLowerCase()
          .includes(this.chain.chainCartUrl.toLowerCase())
      ) {
        // Redirect path is NOT Cart, return SinglePageCheckout to prevent switching to MultiPageCheckout
        return CheckoutPaths.SINGLEPAGE;
      } else {
        return error.error.ui_messaging.button.redirect_uri;
      }
    } else if (isPayPalLandingPage) {
      const urlParams = this.chain.checkoutUrlParameters ? this.chain.checkoutUrlParameters : '';
      return `${this.environment.chk.step1}${urlParams}`;
      // no nested template literals
      // return `${this.environment.chk.step1}${(this.chain.checkoutUrlParameters) ? `${this.chain.checkoutUrlParameters}` : ''}`;
    } else if (error.status === 401) {
      return `${this.chain.chainCartUrl}?OrderId=.&${this.chain.checkoutConfirmationUrlParameters}`;
    } else if (isOrderConfirmationPage) {
      return '/';
    } else {
      return `${this.chain.chainCartUrl}?OrderId=.&${this.chain.checkoutConfirmationUrlParameters}`;
    }
  }

  /**
   * @description
   * TODO: Add unit testing since this is no longer a private method in the checkout resolve guard
   *
   * @param error
   * @param isOrderConfirmationPage
   */
  public getDialogMessageRedirectTextFromError(
    error: HttpErrorResponse | any,
    isOrderConfirmationPage = false,
  ): string {
    if (
      typeof error?.error?.ui_messaging?.message === 'string' &&
      error.error.ui_messaging.message.trim() !== ''
    ) {
      return error.error.ui_messaging.message;
    } else if (error.status && error.status === 401) {
      return 'We are encountering some issues. Please sign in again to return to checkout.';
    } else if (isOrderConfirmationPage) {
      return 'The page you are looking for has expired. Now redirecting you to the home page.';
    } else {
      return 'We are encountering some issues. Now redirecting you back to your cart.';
    }
  }

  public getPaypalExpressToken$(checkoutKey: string): Observable<any> {
    return this.checkoutDao.getPaypalExpressToken$(checkoutKey);
  }

  public processPaypalReturn$(checkoutKey: string, clientIp?: string): Observable<any> {
    return this.checkoutDao.processPaypalReturn$(checkoutKey, clientIp).pipe(
      tap((checkoutResponse: Checkout) => {
        this.store.set('CHECKOUT', checkoutResponse);
      }),
    );
  }

  /**
   * @description applies a promotion or reward code
   * @returns a boolean that represents if its successful so the caller can present error messaging
   */
  public applyPromotionOrRewardCode$(
    promoOrRewardCode: string,
    isCheckout = false,
    isReward = false,
  ): Observable<any> {
    const checkoutKey = this.cookieService.get('X-DCSG-CHECKOUT');
    const actionType = isCheckout ? 'CheckoutAction' : 'CartAction';

    return this.checkoutDao.applyPromotionOrRewardCode$(checkoutKey, promoOrRewardCode).pipe(
      tap((checkoutObject) => {
        this.store.set('CHECKOUT', checkoutObject);
        this.checkoutAnalyticsService.setActionType(
          `Successfully Applied Promo:${promoOrRewardCode}${isReward ? ':Reward' : ''}`,
        );
        this.checkoutAnalyticsService.triggerAnalyticsEvent(actionType);
      }),
      catchError((err: any) => {
        // try to pull the error message from the engine
        if (err.status === 400 && err.error && err.error.message) {
          this.checkoutAnalyticsService.setActionType(
            `Failed to Apply promo - ${err.error?.message} : ${promoOrRewardCode}`,
          );
        }
        if (err.status === 'Fail' && err.error?.message === 'Missing promotion code.') {
          this.checkoutAnalyticsService.setActionType(
            `Failed to Apply promo as Reward Code is missing - ${err.error.message} : ${promoOrRewardCode}`,
          );
        } else {
          this.checkoutAnalyticsService.setActionType(`Failed to Apply promo:${promoOrRewardCode}`);
        }
        this.checkoutAnalyticsService.triggerAnalyticsEvent(actionType);
        return throwError(err);
      }),
    );
  }

  /**
   * @description removes a promotion or reward code
   * @returns a boolean that represents if its successful so the caller can present error messaging
   */
  public removePromotionOrRewardCode$(
    promoOrRewardCode: string,
    isCheckout = false,
  ): Observable<any> {
    const checkoutKey = this.cookieService.get('X-DCSG-CHECKOUT');
    const actionType = isCheckout ? 'CheckoutAction' : 'CartAction';

    return this.checkoutDao.removePromotionOrRewardCode$(checkoutKey, promoOrRewardCode).pipe(
      tap((checkoutObject) => {
        this.store.set('CHECKOUT', checkoutObject);
        this.checkoutAnalyticsService.setActionType(
          `Successfully Removed Promo:${promoOrRewardCode}`,
        );
        this.checkoutAnalyticsService.triggerAnalyticsEvent(actionType);
      }),
      catchError((err: any) => {
        this.checkoutAnalyticsService.setActionType(`Failed to Remove promo:${promoOrRewardCode}`);
        this.checkoutAnalyticsService.triggerAnalyticsEvent(actionType);
        return throwError(err);
      }),
    );
  }

  public finalizePaygateCheckout$ = (checkout: Checkout, flow?: string): Observable<Checkout> => {
    this.cookieService.set('DSG_CLR_ADDRESS', 'Y', undefined, '/');
    return this.checkoutDao.finalizePaygateCheckout$(checkout.checkout_key, flow).pipe(
      tap((checkoutObject: Checkout) => {
        this.store.set('CHECKOUT', checkoutObject);
      }),
      catchError((err: any) => {
        return throwError(err);
      }),
    );
  };

  /**
   * @returns {Observable}
   *
   * @description
   * Function that returns client ip
   */
  public getIp$(): Observable<string> {
    // Make the DAO Request to fire HTTPS call to checkout service.
    return this.checkoutDao.getIp$();
  }

  /**
   * @description submits gifting request
   * @param checkoutKey string
   * @param recipient string
   */
  submitGifting(checkoutKey: string, gift: IGift): Observable<Checkout> {
    return this.checkoutDao.submitGifting(checkoutKey, gift);
  }

  /**
   * @description deletes the gift object
   * @param checkoutKey string
   * @returns new checkout object without gift object
   */

  deleteGifting(checkoutKey: string): Observable<Checkout> {
    return this.checkoutDao.deleteGifting(checkoutKey);
  }

  /**
   * @description enables gifting feature
   * @param checkoutKey string
   * @returns new checkout object with gifting_enabled
   */
  enableGifting(checkoutKey: string): any {
    return this.checkoutDao.enableGifting(checkoutKey);
  }

  submitGiftingConfirmation(checkoutKey: string, gift: IGift) {
    return this.checkoutDao.submitGiftingConfirmation(checkoutKey, gift);
  }

  /**
   * @description adds an additonal person to pickup for BOPIS orders
   * @param checkoutKey string
   * @param proxyData
   */
  public addProxyPickup$(checkoutKey: string, proxyData: ContactForm): any {
    // Ensure white space around email is trimmed
    proxyData.email = this.utilityService.removeWhiteSpace(proxyData.email);
    proxyData = this.transformDataForModernCheckout(proxyData);
    return this.checkoutDao.addProxyPickup$(checkoutKey, proxyData).pipe(
      withLatestFrom(this.store.select('CHECKOUT')),
      tap(([checkoutResponse, currentCheckout]) => {
        const newCheckout = {
          ...currentCheckout,
          proxy_pickup: checkoutResponse.proxy_pickup,
        };
        this.store.set('CHECKOUT', newCheckout);
      }),
    );
  }

  /**
   * @description deletes an existing pickup proxy for given checkout_key
   * @param checkoutKey string
   */
  public deleteProxyPickup$(checkoutKey: string): any {
    return this.checkoutDao.deleteProxyPickup$(checkoutKey).pipe(
      withLatestFrom(this.store.select('CHECKOUT')),
      tap(([checkoutResponse, currentCheckout]) => {
        const newCheckout = {
          ...currentCheckout,
          proxy_pickup: checkoutResponse.proxy_pickup,
        };
        this.store.set('CHECKOUT', newCheckout);
      }),
    );
  }

  /**
   * @description
   * Accepts an array of error message objects from Payments and then displays them on the page
   *
   * @param errors
   */
  /* istanbul ignore next */
  public displayErrorMessages = (errors: PaymentError[]) => {
    this.independencePayErrors.next(errors);
  };

  /**
   * Redirects to the order confirmation page
   */

  /* istanbul ignore next */
  public redirectToOrderConfirmation = (queryParams: string): void => {
    this.window.location.assign(
      `${this.environment.chk.step3}?${this.chain.checkoutConfirmationUrlParameters}${queryParams}`,
    );
  };

  /**
   * @description
   * Accepts an object with query parameters and will iterate through them
   * and append them to the Order Review page URL and then perform the redirect
   * to the Order Review page.
   *
   * @param queryParameters
   *
   * @note `indPay=true` set by default so the checkout-resolve.guard.ts knows athlete is in Independence Pay Flow when returning from paypal. Analytic window variables don't work in checkout-resolve.guard.ts
   */

  /* istanbul ignore next */
  public showOrderReview = (queryParameters: QueryParameters): void => {
    let params = '&';
    const queryArr = Object.entries(queryParameters);

    for (let i = 0; i < queryArr.length; i++) {
      for (let j = 0; j < queryArr[i].length; j++) {
        if (j === 0) {
          params += `${queryArr[i][j]}=`;
        } else {
          params += queryArr[i][j];
        }
      }
      if (queryArr[i + 1]) {
        params += '&';
      }
    }

    this.window.location.assign(`${this.environment.chk.step2}${params}&indPay=true`);
  };

  /**
   * @description
   * Method exposed to PaymentsJS for updating address and communicates result
   * back to Payments.
   *
   * @param billingAddress
   * @param shippingAddress
   */

  /* istanbul ignore next */
  public updateAddress = (
    billingAddress: PaymentsAddress,
    shippingAddress: PaymentsAddress = null,
    forceReject = false,
  ): Promise<any> => {
    const paymentsBillAddressResponse: any = {};
    const paymentsShipAddressResponse: any = {};
    let checkoutKey = '';

    if (forceReject) {
      return Promise.reject('Failed to update address');
    } else {
      // If shipping address isn't empty make call for both, otherwise return the billing response and
      return this.avsService
        .verifyAddress$(billingAddress)
        .pipe(
          tap((billAvsResponse) => {
            paymentsBillAddressResponse.avsResponse = billAvsResponse;
            if (billAvsResponse.avsResult.decision === 'ACCEPT') {
              paymentsBillAddressResponse.formattedAddress = {
                first_name: billingAddress.first_name,
                last_name: billingAddress.last_name,
                address: billAvsResponse.verifiedAddress.addressLines[0],
                city: billAvsResponse.verifiedAddress.city,
                state: billAvsResponse.verifiedAddress.state,
                zipcode: billAvsResponse.verifiedAddress.zipCode,
                country: billingAddress.country || 'US',
                phone: billingAddress.phone,
                email: billingAddress.email,
              };
              paymentsBillAddressResponse.avsChecked = true;
            }
          }),
          mergeMap((billingAvsResponse) =>
            iif(
              () => shippingAddress !== null,
              this.avsService.verifyAddress$(shippingAddress),
              of(billingAvsResponse),
            ),
          ),
          tap((shipAvsResponse) => {
            paymentsShipAddressResponse.avsResponse = shipAvsResponse;
            if (shippingAddress !== null && shipAvsResponse.avsResult.decision === 'ACCEPT') {
              paymentsShipAddressResponse.formattedAddress = {
                first_name: shippingAddress.first_name,
                last_name: shippingAddress.last_name,
                address: shipAvsResponse.verifiedAddress.addressLines[0],
                city: shipAvsResponse.verifiedAddress.city,
                state: shipAvsResponse.verifiedAddress.state,
                zipcode: shipAvsResponse.verifiedAddress.zipCode,
                country: shippingAddress.country || 'US',
                phone: shippingAddress.phone,
                email: shippingAddress.email,
              };
              paymentsShipAddressResponse.avsChecked = true;
            }
          }),
          mergeMap((avsResponse) => {
            return this.store.select('CHECKOUT').pipe(
              take(1),
              tap((checkout: Checkout) => {
                checkoutKey = checkout.checkout_key;
              }),
            );
          }),
          concatMap((checkout) => {
            if (paymentsBillAddressResponse.avsResponse.avsResult.decision !== 'ACCEPT') {
              return throwError({
                status: 'Fail',
                errors: [
                  {
                    message: 'Invalid Billing Address',
                  },
                ],
              });
            }
            return this.checkoutDao.addAddress$(
              checkoutKey,
              shippingAddress !== null ? 'billing' : 'shippingbilling',
              paymentsBillAddressResponse.formattedAddress,
            );
          }),
          concatMap((checkoutAddresses) => {
            if (
              paymentsShipAddressResponse &&
              paymentsShipAddressResponse.avsResponse.avsResult.decision !== 'ACCEPT'
            ) {
              return throwError({
                status: 'Fail',
                errors: [
                  {
                    message: 'Invalid Shipping Address',
                  },
                ],
              });
            }

            return shippingAddress !== null
              ? this.checkoutDao.addAddress$(
                  checkoutKey,
                  'shipping',
                  paymentsShipAddressResponse.formattedAddress,
                )
              : of(checkoutAddresses);
          }),
          concatMap((checkout: Checkout) => {
            return this.checkoutDao.finalizeAddresses$(checkout.checkout_key);
          }),
          map((checkout) => {
            // return this object to payments. they don't need any thing else
            return { status: 'SUCCESS' };
          }),
          catchError((err) => {
            // Error returned from avs
            if (err.errors) {
              return throwError(err);
            } else if (err.error.message) {
              // errors from checkout
              return throwError({
                status: 'Fail',
                errors: [
                  {
                    message: err.error.message,
                  },
                ],
              });
            } else {
              // general errors
              return throwError(err);
            }
          }),
        )
        .toPromise();
    }
  };

  /**
   * @description
   * Method exposed to PaymentsJS for add and finalize address and communicates result
   * back to Payments.
   *
   * @param checkoutKey
   * @param addressType
   * @param userAddress
   */

  public addAndFinalizeCheckoutAddress(
    checkoutKey: string,
    addressType: string,
    userAddress: CCAddressV2,
  ): Promise<any> {
    return this.checkoutDao
      .addAndFinalizeAddress$(checkoutKey, addressType, userAddress)
      .pipe(
        map((checkout) => {
          // return this object to payments. they don't need any thing else
          return { status: 'SUCCESS', checkout: checkout };
        }),
        catchError((err) => {
          // Error returned from avs
          if (err.errors) {
            return throwError(err);
          } else if (err.error.message) {
            // errors from checkout
            return throwError({
              status: 'Fail',
              errors: [
                {
                  message: err.error.message,
                },
              ],
            });
          } else {
            // general errors
            return throwError(err);
          }
        }),
      )
      .toPromise();
  }

  /**
   * @description
   * Method exposed to PaymentsJS for add addresses and communicates result
   * back to Payments.
   *
   * @param checkoutKey
   * @param addressType
   * @param userAddress
   */

  public addAddressToCheckout(
    checkoutKey: string,
    addressType: string,
    userAddress: CommerceAddress,
  ): Promise<any> {
    return this.checkoutDao
      .addAddress$(checkoutKey, addressType, userAddress)
      .pipe(
        map((checkout) => {
          // return this object to payments. they don't need any thing else
          return { status: 'SUCCESS', checkout: checkout };
        }),
        catchError((err) => {
          // Error returned from avs
          if (err.errors) {
            return throwError(err);
          } else if (err.error.message) {
            // errors from checkout
            return throwError({
              status: 'Fail',
              errors: [
                {
                  message: err.error.message,
                },
              ],
            });
          } else {
            // general errors
            return throwError(err);
          }
        }),
      )
      .toPromise();
  }

  /**
   * @description
   * Method exposed to PaymentsJS for finalizing addresses and communicates result
   * back to Payments.
   *
   * @param checkoutKey
   */

  public finalizeCheckoutAddresses(checkoutKey: string): Promise<any> {
    return this.checkoutDao
      .finalizeAddresses$(checkoutKey)
      .pipe(
        map((checkout) => {
          // return this object to payments. they don't need any thing else
          return { status: 'SUCCESS' };
        }),
        catchError((err) => {
          // Error returned from avs
          if (err.errors) {
            return throwError(err);
          } else if (err.error.message) {
            // errors from checkout
            return throwError({
              status: 'Fail',
              errors: [
                {
                  message: err.error.message,
                },
              ],
            });
          } else {
            // general errors
            return throwError(err);
          }
        }),
      )
      .toPromise();
  }

  /* istanbul ignore next */
  public authorizePaygate(
    checkoutKey: string,
    paygateReference: string,
    paymentType = '',
    savedIndicator = '',
  ) {
    return this.checkoutDao.authorizePaygate(
      checkoutKey,
      paygateReference,
      paymentType,
      savedIndicator,
    );
  }

  // endregion

  // region Modern Checkout

  /**
   * @description create a modern checkout object.
   */
  private createModernCheckout$(): Observable<Checkout> {
    return this.checkoutDao.createModernCheckout$().pipe(
      map((checkoutResponse: Checkout) => this.setScoreCardPlus(checkoutResponse)),
      tap((checkoutResponse: Checkout) => {
        this.store.set('CHECKOUT', checkoutResponse);
      }),
    );
  }

  /**
   * @description get an existing modern checkout object.
   *
   * 410: Order already submitted
   * 426: Checkout key is invalid
   * 0: Unknown client error
   */
  private getModernCheckout$(checkoutKey: string): Observable<Checkout> {
    if (checkoutKey === undefined || checkoutKey === null || checkoutKey === '') {
      // need this logic for the ship mode restriction error handling. If there is not a checkout key in the cookie store, we create a checkout object instead.
      return this.createModernCheckout$();
    }

    return this.checkoutDao.getModernCheckout$(checkoutKey).pipe(
      map((checkoutResponse: Checkout) => this.setScoreCardPlus(checkoutResponse)),
      tap((checkoutResponse: Checkout) => {
        this.store.set('CHECKOUT', checkoutResponse);
      }),
      tap((checkoutResponse: Checkout) => {
        if (!this.environment.killSwitch?.honeycombEnabled?.[this.chain.chainIdentifierAbbr])
          return;

        const hasWarrantyAddons = checkoutResponse.cart.items.some((item: CartItem) => {
          return item.addons ? item.addons.some((addon) => addon.type === 'warranty') : false;
        });

        const hasWarrantyAvailability = checkoutResponse.cart.items.some(
          (item: CartItem) => (item.availableWarranties?.length ?? 0) > 0,
        );

        this.tracerService.log('checkout_warranty_availability_completed', {
          'warranty.is_eligible': true,
          'warranty.addons_success': hasWarrantyAddons,
          'warranty.availability_success': hasWarrantyAvailability,
        });
      }),
      catchError((err) => {
        if (typeof err.status !== 'undefined') {
          if (err.status === 410) {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              `HTTP Status 410: Error getting checkout by key; Checkout key has already been submitted, creating a new checkout`,
              err,
              { CheckoutKey: checkoutKey },
            );

            return this.createModernCheckout$();
          } else if (err.status === 426) {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              `HTTP Status 426: Error getting checkout by key; Invalid checkout key, deleting checkout cookie from browser and creating a new checkout`,
              err,
              { CheckoutKey: checkoutKey },
            );

            this.cookieService.delete('X-DCSG-CHECKOUT', '/');

            return this.createModernCheckout$();
          } else if (err.status === 0) {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              `HTTP Status 0: Error getting checkout by key`,
              err,
              { CheckoutKey: checkoutKey },
            );
          }
        } else {
          this.checkoutAnalyticsService.generateLogError(
            CheckoutErrorLogKeys.checkoutService,
            `HTTP Status Unspecified: Error getting checkout by key`,
            err,
            { CheckoutKey: checkoutKey },
          );
        }
        if (this.environment.killSwitch?.honeycombEnabled?.[this.chain.chainIdentifierAbbr]) {
          this.tracerService.log('checkout_warranty_availability_completed', {
            'warranty.availability_success': false,
            'warranty.addons_success': false,
          });
        }

        return throwError(err);
      }),
    );
  }

  /**
   * @description get an existing submitted modern checkout object.
   *
   *
   * 426: Unable to access requested submitted checkout object
   */
  private getSubmittedModernCheckout$(checkoutKey: string): Observable<Checkout> {
    if (checkoutKey === undefined || checkoutKey === null || checkoutKey === '') {
      // need this logic for the ship mode restriction error handling. If there is not a checkout key in the cookie store, we create a checkout object instead.
      return this.createModernCheckout$();
    }

    return this.checkoutDao.getSubmittedModernCheckout$(checkoutKey).pipe(
      map((checkoutResponse: Checkout) => this.setScoreCardPlus(checkoutResponse)),
      map((checkoutResponse: Checkout) =>
        this.updateCheckoutWithSameBillingShipping(checkoutResponse),
      ),
      catchError((err) => {
        if (typeof err.status !== 'undefined') {
          if (err.status === 410) {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              `HTTP Status 410: Error getting checkout by key; Checkout key has already been submitted, creating a new checkout`,
              err,
              { CheckoutKey: checkoutKey },
            );

            return this.createModernCheckout$();
          } else if (err.status === 426) {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              `HTTP Status 426: Error getting checkout by key; Invalid checkout key, deleting checkout cookie from browser and creating a new checkout`,
              err,
              { CheckoutKey: checkoutKey },
            );

            this.cookieService.delete('X-DCSG-CHECKOUT', '/');

            return this.createModernCheckout$();
          } else if (err.status === 0) {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              `HTTP Status 0: Error getting checkout by key`,
              err,
              { CheckoutKey: checkoutKey },
            );
          }
        } else {
          this.checkoutAnalyticsService.generateLogError(
            CheckoutErrorLogKeys.checkoutService,
            `HTTP Status Unspecified: Error getting checkout by key`,
            err,
            { CheckoutKey: checkoutKey },
          );
        }

        return throwError(err);
      }),
    );
  }

  /**
   * @param {{} | {originIdentifier: string, referenceCode: string, {addressLines: string[], city: string, state: string, zipcode: string, country: string}}} CheckoutAddress - AVS verified User entered address
   * @returns {Observable<Checkout>}
   *
   * @description
   * Function that calls the CheckoutDAO to call Checkout Engine add addresses API.  This coarse-grain API can accept an empty Object {}, or
   * an Object with User address {'address': 'STRING', 'city': 'STRING', 'state': 'STRING', 'zipcode': 'STRING', 'country': 'STRING', 'phone': 'STRING', 'email': 'STRING', 'external_key': 'STRING', , 'external_source': 'WCSAPI' }.
   */
  private addAndFinalizeAddress$(
    checkout: Checkout,
    addressType: string,
    userContactData: any,
    userAddressData: any,
  ): Observable<Checkout> {
    // Splitting fullname to First Name and Last Name.
    let lName: string = null;
    let fName: string = null;

    if ('shipping' !== addressType) {
      // Will be populated with address data when user contact doesn't exists and a address type isn't specified
      lName = userContactData.last_name || userAddressData.last_name;
      fName = userContactData.first_name || userAddressData.first_name;
    } else if ('shipping' === addressType) {
      lName = userAddressData.last_name;
      fName = userAddressData.first_name;
    }

    // Constructing request address for checkout-engine
    const userAddress: CCAddressV2 = {
      firstName: fName,
      lastName: lName,
      address: userAddressData.address,
      city: userAddressData.city,
      state: userAddressData.state,
      zipcode: userAddressData.zipcode,
      country: userAddressData.country || 'US',
      phone: userContactData.phone,
      email: this.utilityService.removeWhiteSpace(userContactData.email),
    };

    // Find a corresponding address in the checkout object based on type
    let existingUserAddress: AddressV3 = null;
    if (
      'billing' !== addressType &&
      'shipping' !== addressType &&
      checkout.addresses.SHIPPINGBILLING
    ) {
      existingUserAddress = checkout.addresses.SHIPPINGBILLING;
    } else if ('billing' === addressType && checkout.addresses.BILLING) {
      existingUserAddress = checkout.addresses.BILLING;
    } else if ('shipping' === addressType && checkout.addresses.SHIPPING) {
      existingUserAddress = checkout.addresses.SHIPPING;
    }

    // Check if the existing address matches
    if (null !== existingUserAddress) {
      try {
        let alreadyExists = true;

        // Iterate through the properties of the new address to add
        for (const i in userAddress) {
          // If any of the properties of the userAddress dont match the corresponding
          // address from the checkout object, continue with integrating the new address
          if (userAddress[i] !== existingUserAddress[i]) {
            alreadyExists = false;
          }
        }

        // If the new address has a match, dont send it
        if (true === alreadyExists) {
          return this.store.selectDistinctKey('CHECKOUT').pipe(take(1));
        }
      } catch (error) {
        return throwError(error);
      }
    }

    // Make the DAO Request to fire HTTPS call to checkout addAddresses service.
    return this.checkoutDao
      .addAndFinalizeAddress$(checkout.checkout_key, addressType, userAddress)
      .pipe(
        tap((checkoutObject) => {
          this.store.set('CHECKOUT', checkoutObject);
        }),
        catchError((err: any) => {
          if (typeof err.name === 'string' && err.name.toLowerCase === 'timeouterror') {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              'Error adding address to checkout object: timeout',
              err,
            );
          } else {
            this.checkoutAnalyticsService.generateLogError(
              CheckoutErrorLogKeys.checkoutService,
              'Error adding address to checkout object',
              err,
            );
          }
          return throwError(err);
        }),
      );
  }

  private submitCheckout$(checkout: Checkout, flow?: string): Observable<Checkout> {
    return this.checkoutDao.submitCheckout$(checkout.checkout_key, flow).pipe(
      tap((checkoutObject: Checkout) => {
        this.store.set('CHECKOUT', Object.assign(checkoutObject, { finalized: true }));
      }),
      catchError((err: any) => {
        return throwError(err);
      }),
    );
  }

  public get disablePlacerOrderButton(): Observable<boolean> {
    return this.enablePlaceOrderButton$.asObservable();
  }

  public togglePlaceOrderButton(enabled: boolean) {
    this.enablePlaceOrderButton$.next(enabled);
  }

  // endregion

  // region Independence Pay

  /**
   * @description
   * Accepts and authorization response from Payments and then places the order
   * and communicates result back to Payments.
   *
   * @param authorizationResponse
   */
  /* istanbul ignore next */
  public continueWithOrderPlacement = (
    authorizationResponse: AuthorizationResponse,
  ): Promise<any> => {
    let checkoutObject = null;
    let checkoutKey = null;
    return this.store
      .selectDistinctDefinedKey('CHECKOUT')
      .pipe(
        take(1),
        tap((checkout: Checkout) => {
          checkoutObject = checkout;
          checkoutKey = checkoutObject.checkout_key;
        }),
        concatMap(
          () => {
            // If the flow is modern, we don't need to authorize the checkout
            return of(null);
          },
          () => {
            // If the flow is modern, we need to finalize the checkout
            return this.submitCheckout$(
              checkoutObject,
              authorizationResponse.authorizationResponse.paymentMethods[0].paymentMethod,
            ).pipe(
              take(1),
              tap(() => {
                this.paymentService.storePaymentMethodsInLocalStorage(
                  authorizationResponse.authorizationResponse.paymentMethods,
                  checkoutKey,
                );
                return {
                  status: 'SUCCESS',
                };
              }),
              catchError((err) => {
                return throwError({
                  status: 'Fail',
                  errors: [
                    {
                      message: err.error.message,
                    },
                  ],
                });
              }),
            );
          },
        ),
      )
      .toPromise()
      .then((checkout: Observable<any>): Promise<any> => {
        return checkout.toPromise();
      });
  };

  /**
   * @description get an existing a checkout object for Payments team.
   *
   * 410: Order already submitted
   * 426: Checkout key is invalid
   * 0: Unknown client error
   */

  /* istanbul ignore next */
  public getCheckoutsForPayments = () => {
    let checkoutKey = '';

    return this.store
      .selectDistinctDefinedKey('CHECKOUT')
      .pipe(
        take(1),
        tap((checkout: Checkout) => {
          checkoutKey = checkout.checkout_key;
        }),
        concatMap((checkout: Checkout) => {
          return this.getCheckout$(checkoutKey).pipe(
            map((checkout) => {
              return checkout;
            }),
            catchError((err) => {
              if (typeof err.status !== 'undefined') {
                if (err.status === 410) {
                  this.checkoutAnalyticsService.generateLogError(
                    CheckoutErrorLogKeys.PAYMENTS_BUTTONS_JS,
                    `Independence Pay - HTTP Status 410: Error getting checkout by key; Checkout key has already been submitted, creating a new checkout`,
                    err,
                    { CheckoutKey: this.checkoutKey },
                  );

                  return this.createCheckoutAsPromise();
                } else if (err.status === 426) {
                  this.checkoutAnalyticsService.generateLogError(
                    CheckoutErrorLogKeys.PAYMENTS_BUTTONS_JS,
                    `Independence Pay - HTTP Status 426: Error getting checkout by key; Invalid checkout key, deleting checkout cookie from browser and creating a new checkout`,
                    err,
                    { CheckoutKey: this.checkoutKey },
                  );

                  this.cookieService.delete('X-DCSG-CHECKOUT', '/');

                  return this.createCheckoutAsPromise();
                } else if (err.status === 0) {
                  this.checkoutAnalyticsService.generateLogError(
                    CheckoutErrorLogKeys.PAYMENTS_BUTTONS_JS,
                    `Independence Pay - HTTP Status 0: Error getting checkout by key`,
                    err,
                    { CheckoutKey: this.checkoutKey },
                  );
                }
              } else {
                this.checkoutAnalyticsService.generateLogError(
                  CheckoutErrorLogKeys.PAYMENTS_BUTTONS_JS,
                  `Independence Pay - HTTP Status Unspecified: Error getting checkout by key`,
                  err,
                  { CheckoutKey: this.checkoutKey },
                );
              }

              return throwError({
                status: 'fail',
                errors: [
                  {
                    message: err.message.length ? err.message : 'Failed to get checkout object',
                  },
                ],
              });
            }),
          );
        }),
      )
      .toPromise();
  };

  /**
   * @description add a promo code for Independence Pay
   * @param promoCode Indpendence Pay promo code
   * @returns Checkout as a promise
   * 200: applied
   * 400: not applied/no dcsg-cart
   * 405: put promotion call made without a checkout key in path
   * 410: already submitted
   * 418: Commerce issue when applying
   * 422: cart changed
   * 426: checkout object invalid
   * 500 internal error
   */

  /* istanbul ignore next */
  public addIndPayPromo = (promoCode: string) => {
    let checkoutKey = null;
    const daoFn$ = this.checkoutDao.applyPromotionOrRewardCode$;

    return this.store
      .selectDistinctDefinedKey('CHECKOUT')
      .pipe(
        take(1),
        tap((checkout: Checkout) => {
          checkoutKey = checkout.checkout_key;
        }),
        concatMap((checkout: Checkout) => {
          return daoFn$(checkoutKey, promoCode).pipe(
            tap((checkoutResponse: Checkout) => {
              this.store.set('CHECKOUT', checkoutResponse);
            }),
            map((checkout: Checkout) => {
              this.loadingOverlayService.hide();
              if (checkout.messaging) {
                throw new Error(checkout.messaging.message);
              } else {
                return {
                  status: 'SUCCESS',
                  checkout: checkout,
                };
              }
            }),
          );
        }),
        catchError((err) => {
          this.loadingOverlayService.hide();
          let errorMessage = null;
          switch (err.status) {
            case 400:
              errorMessage = err?.error?.message;
              break;
            case 405:
              errorMessage = 'Missing CheckoutKey';
              break;
            case 410:
              errorMessage = 'Promo Code was already submitted';
              break;
            case 418:
              errorMessage = 'Commerce Issue when Applying';
              break;
            case 422:
              errorMessage = 'Cart Changed';
              break;
            case 426:
              errorMessage = 'Checkout Object Invalid';
              break;
            case 500:
              errorMessage = 'Internal Error';
              break;
            default:
              errorMessage = 'Bad Request';
              break;
          }

          // if the error doesn't have a status code, it's an error from the checkout service
          if (!err.status) {
            errorMessage = err;
          }

          return throwError({
            status: 'Fail',
            errors: [
              {
                message: errorMessage,
              },
            ],
          });
        }),
      )
      .toPromise();
  };

  /**
   * @description removes a promo code for Independence Pay
   * @param promoCode Independence Pay promo code
   * @returns Checkout as a promise
   * 200: deleted
   * 400: not deleted/no dcsg-cart
   * 405: delete promotion call made without a checkout key in path
   */

  /* istanbul ignore next */
  public removeIndPayPromo = (promoCode: string) => {
    let checkoutKey = null;
    const daoFn$ = this.checkoutDao.removePromotionOrRewardCode$;

    return this.store
      .selectDistinctDefinedKey('CHECKOUT')
      .pipe(
        take(1),
        tap((checkout: Checkout) => {
          checkoutKey = checkout.checkout_key;
        }),
        concatMap((checkout: Checkout) => {
          return daoFn$(checkoutKey, promoCode).pipe(
            tap((checkoutResponse: Checkout) => {
              this.store.set('CHECKOUT', checkoutResponse);
            }),
            map((checkout: Checkout) => {
              this.loadingOverlayService.hide();
              return {
                status: 'SUCCESS',
                checkout: checkout,
              };
            }),
          );
        }),
        catchError((err) => {
          this.loadingOverlayService.hide();
          let errorMessage = null;
          switch (err.status) {
            case 400:
              errorMessage = 'The Promo Code was not deleted';
              break;
            case 405:
              errorMessage = 'Missing CheckoutKey';
              break;
            default:
              errorMessage = 'Bad Request';
              break;
          }

          // if the error doesn't have a status code, it's an error from the checkout service
          if (!err.status) {
            errorMessage = err;
          }

          return throwError({
            status: 'Fail',
            errors: [
              {
                message: errorMessage,
              },
            ],
          });
        }),
      )
      .toPromise();
  };

  public submitPaypal(checkout: Checkout, authorizationResponse: AuthorizationResponse) {
    return this.submitCheckout$(
      checkout,
      authorizationResponse.authorizationResponse.paymentMethods[0].paymentMethod,
    ).pipe(
      take(1),
      tap((checkout) => {
        return {
          status: 'SUCCESS',
          checkout,
        };
      }),
      catchError((err) => {
        return throwError({
          status: 'Fail',
          errors: [
            {
              message: err.error.message,
            },
          ],
        });
      }),
    );
  }

  private submitModernTipAmount(checkoutKey: string, tipAmount: string): Observable<Checkout> {
    return this.checkoutDao.submitModernTipAmount$(checkoutKey, tipAmount).pipe(
      tap((checkoutObject: Checkout) => {
        this.store.set('CHECKOUT', Object.assign(checkoutObject));
      }),
      catchError((err: any) => {
        return throwError(err);
      }),
    );
  }

  private transformDataForModernCheckout(proxyData: ContactForm): any {
    return {
      firstName: proxyData.first_name,
      lastName: proxyData.last_name,
      email: proxyData.email,
    };
  }

  // endregion

  private updateCheckoutWithSameBillingShipping(checkout: Checkout): Checkout {
    const sameBillingShipping = !!checkout.addresses?.SHIPPINGBILLING;
    const shipping = {
      ...checkout.shipping,
      sameBillingShipping,
    };
    const newCheckout = {
      ...checkout,
      shipping,
    };
    this.store.set('CHECKOUT', newCheckout);
    return newCheckout;
  }

  private setScoreCardPlus(checkout: Checkout): Checkout {
    return {
      ...checkout,
      cart: {
        ...checkout.cart,
        items: checkout.cart?.items?.map((item) => ({
          ...item,
          isScoreCardPlus: this.scorecardPlusConfigService.isScoreCardPlusSku(item.sku),
        })),
      },
    };
  }
}
